<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RoutePlan } from '@/api/route-plans';
import { useAssignmentsByCaregiver } from '@/composables/useAssignmentsByCaregiver';
import { useCaregiverColors } from '@/composables/useRouteColors';
import RoutePlanRouteViews from './route-plan-route-views.vue';

const { t } = useI18n();

interface Props {
  routePlan: RoutePlan;
}

const props = defineProps<Props>();

const appliedAssignments = computed(
  () => (props.routePlan.routeAssignments || []).filter(a => a.status === 'applied'),
);

const assignmentsByCaregiver = useAssignmentsByCaregiver(() => props.routePlan);
const { colorMap } = useCaregiverColors(assignmentsByCaregiver);

const caregiverCount = computed(() => Object.keys(assignmentsByCaregiver.value).length);
</script>

<template>
  <div>
    <v-alert
      type="success"
      prominent
      class="mb-4"
    >
      <div class="text-h6">
        {{ t('routePlan.shiftsCreatedSuccessfully', { count: appliedAssignments.length }) }}
      </div>
      <div class="text-subtitle-2">
        {{ caregiverCount }} {{ t('routePlan.caregivers') }}
        &middot;
        {{ routePlan.humanTotalTravelTime }}
        {{ t('routePlan.totalTravelTime').toLowerCase() }}
      </div>
    </v-alert>

    <!-- Read-only map / timeline / list for reference; no actions. -->
    <route-plan-route-views
      :route-plan="routePlan"
      :assignments-by-caregiver="assignmentsByCaregiver"
      :color-map="colorMap"
      read-only
    />
  </div>
</template>
